import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import {
  Trophy, TrendingUp, DollarSign, Target, CheckCircle2, Mail, StickyNote,
  Loader2, Crown, Medal, Award, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RepPerformance = () => {
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const fetchPerformance = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/team/performance`, {
        params: { period },
        withCredentials: true
      });
      setPerformance(data.performance || []);
    } catch (err) {
      console.error('Failed to fetch performance:', err);
    }
    setLoading(false);
  }, [period]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return `$${parseFloat(value).toLocaleString()}`;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown size={18} style={{ color: '#F59E0B' }} />;
    if (rank === 2) return <Medal size={18} style={{ color: '#9CA3AF' }} />;
    if (rank === 3) return <Award size={18} style={{ color: '#CD7F32' }} />;
    return <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6B7280' }}>{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)';
    if (rank === 2) return 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)';
    if (rank === 3) return 'linear-gradient(135deg, #FED7AA 0%, #FDBA74 100%)';
    return '#fff';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '3rem' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  return (
    <div data-testid="rep-performance">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Trophy size={22} style={{ color: '#F59E0B' }} />
            Sales Leaderboard
          </h2>
          <p style={{ fontSize: '0.813rem', color: '#9CA3AF', margin: 0 }}>
            Performance metrics by team member
          </p>
        </div>
        
        {/* Period Selector */}
        <div style={{ display: 'flex', gap: '0.25rem', background: '#E5E7EB', borderRadius: 6, padding: 2 }}>
          {['week', 'month', 'quarter'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`period-${p}`}
              style={{
                padding: '0.375rem 0.75rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 500,
                border: 'none', cursor: 'pointer', textTransform: 'capitalize',
                background: period === p ? '#fff' : 'transparent',
                color: period === p ? 'var(--navy)' : '#6B7280'
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      {performance.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF' }}>
          No team members found
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {performance.map((rep, i) => (
            <div
              key={rep.id}
              data-testid={`rep-card-${i}`}
              style={{
                background: getRankBg(rep.rank),
                borderRadius: 10,
                border: rep.rank <= 3 ? 'none' : '1px solid #E5E7EB',
                padding: '1rem 1.25rem',
                boxShadow: rep.rank <= 3 ? '0 2px 8px rgba(0,0,0,0.06)' : 'none'
              }}
            >
              {/* Rep Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.875rem' }}>
                {/* Rank */}
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: rep.rank <= 3 ? 'rgba(255,255,255,0.8)' : '#F3F4F6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {getRankIcon(rep.rank)}
                </div>
                
                {/* Name & Role */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--navy)' }}>
                    {rep.name}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#6B7280', textTransform: 'capitalize' }}>
                    {rep.role?.replace('_', ' ')} • {rep.email}
                  </div>
                </div>
                
                {/* Total Revenue */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>
                    Revenue
                  </div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>
                    {formatCurrency(rep.total_revenue)}
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem',
                background: rep.rank <= 3 ? 'rgba(255,255,255,0.6)' : '#FAFBFC',
                borderRadius: 8, padding: '0.75rem'
              }}>
                {/* Deals Won */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '0.125rem' }}>
                    <Target size={12} style={{ color: '#10B981' }} />
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--navy)' }}>{rep.deals_won}</span>
                  </div>
                  <div style={{ fontSize: '0.6rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Won</div>
                </div>
                
                {/* Win Rate */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '0.125rem' }}>
                    <TrendingUp size={12} style={{ color: '#3B82F6' }} />
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--navy)' }}>{rep.win_rate}%</span>
                  </div>
                  <div style={{ fontSize: '0.6rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Win Rate</div>
                </div>
                
                {/* Tasks Completed */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '0.125rem' }}>
                    <CheckCircle2 size={12} style={{ color: '#8B5CF6' }} />
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--navy)' }}>{rep.tasks_completed}</span>
                  </div>
                  <div style={{ fontSize: '0.6rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Tasks</div>
                </div>
                
                {/* Emails Sent */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '0.125rem' }}>
                    <Mail size={12} style={{ color: '#0078D4' }} />
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--navy)' }}>{rep.emails_sent}</span>
                  </div>
                  <div style={{ fontSize: '0.6rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Emails</div>
                </div>
                
                {/* Activities */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '0.125rem' }}>
                    <Activity size={12} style={{ color: '#F59E0B' }} />
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--navy)' }}>{rep.activities_count}</span>
                  </div>
                  <div style={{ fontSize: '0.6rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Activities</div>
                </div>
              </div>
              
              {/* Secondary Stats */}
              <div style={{
                display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.7rem', color: '#6B7280'
              }}>
                <span>{rep.total_assigned} deals assigned</span>
                <span>{rep.deals_lost} lost</span>
                <span>{rep.notes_added} notes</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RepPerformance;
